import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { ResyncPreview } from '../components/channels/ResyncDialog';
import type { Profitability } from '../components/profitability/types';
import type {
  Machine, SaveMachine, ShiftEntry, SaveShiftEntry, ProductionKpis, FactoryCosts, FactoryCostSettings, ProductCost,
  SaveProductCostProfile, OutworkWorker, SaveOutworkWorker, OutworkMovement, OutworkType, OutworkRate, OutworkSettlement,
  OutworkPayment, CustomerPlant, CustomerOrder, SaveCustomerOrder, SaveCustomerOrderDelivery, DebtPlan, SaveDebtPlan,
  GenerateInstallments, DebtInstallment, DebtSummary, EquipmentOverview, MaintenancePlan, SaveMaintenancePlan,
  MaintenanceRecord, SaveMaintenanceRecord, CompleteMaintenance, MaintenanceCalendarItem, StockStatus, StockLevels,
  PlanLine, PlanResult, StorageLocation, StorageItem, SaveLocationMovement, LocationMovement, MaterialUse, MaterialIssue,
  ScaleReadResult, Reception, FactoryMaterial, MaterialLot, MixRecipe, MixBatch, MixTrace, LotTrace, MaterialOrigin,
  SaveReception, DifferenceTreatment, GrindingTrip, SaveGrindingOut, SaveGrindingReturn, SalesPrices, TaxBookSummary, TaxBookEntry, CostClose, SaveCostClose, FactoryResults,
  ProductLotConfig, ProductLot, WeighIntoLot, WeighResult, OpenLot, CreatePallet, ProductLotTrace, MachineSchedule, SaveMachineSchedule, ScheduleForecast,
  TelemetryLive, MachineStopView, MachineConnection,
} from '../types/factory';
import {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  GoogleAuthRequest,
  Product,
  ProductVariant,
  CreateProductRequest,
  ProductListResponse,
  ProductStats,
  PaginationParams,
  Tenant,
  RegisterSaleRequest,
  RegisterSaleResponse,
  FeatureFlag,
  SaleFeedResponse,
  OrderListResponse,
  OrderDetail,
  User,
  ProductionOrder,
  RetentionSample,
  CustomerAnalytics,
  CommercialToday,
  StockCoverage,
  CallEvent,
  LotProfitabilityReport,
  CustomerInactivityAlert,
  ProductSeasonalityReport,
  Warehouse,
  ProductTraceability,
  CalendarEvent,
  LiabilitiesSummary,
  ReceivablesSummary,
  PaymentCheck,
  PaymentCheckCreate,
  CustomerAccountSummary,
  CustomerStatement,
  CustomerOrderBalance,
  CustomerPaymentList,
  CustomerPaymentDto,
  RegisterCustomerPayment,
  SalesForecastResponse,
  YoYComparisonResponse,
  QuarterlyBreakdownResponse,
  SalesByCustomerTypeResponse,
  StrategicClientsReportResponse,
  ImportMondaySalesRequest,
  ImportMondaySalesResult
} from '../types';
import type { Recipe, RecipeListResponse, CreateRecipeRequest, Material, Service } from '../types';
import type { InvoicingConfig, RepresentativeInfo, VerifyConnectionResult, InvoicePreview, EmitInvoiceRequest, FiscalInvoice, FiscalSummary } from '../types';
import type { PosProduct, PosSaleRequest, PosSaleResult, CashRegisterSummary } from '../types';
import type { Development } from '../types';
import type { FixedExpenseRow, FixedExpenseInput, OneTimeExpenseRow, OneTimeExpenseInput, ExpensesMonthly, PendingDelivery } from '../types';

/** Una fila del historial de precios: sin cliente = precio de lista; con cliente = pactado (0 = se lo quitó). */
export type PriceHistoryRow = {
  id: number; productId: number; productName: string; customerId: number | null; customerName: string | null;
  price: number; effectiveFrom: string; source: string; note: string | null; createdAt: string;
};

// API Configuration
// Default to relative /api so production deployments don't fall back to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token and tenant headers
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        try {
          const tenantStr = localStorage.getItem('auth_tenant');
          if (tenantStr) {
            const tenant = JSON.parse(tenantStr);
            if (tenant?.subdomain) {
              config.headers['X-Tenant-Subdomain'] = tenant.subdomain;
            }
            if (tenant?.id) {
              config.headers['X-Tenant-ID'] = String(tenant.id);
            }
          }
        } catch {}
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle common errors
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          localStorage.removeItem('auth_tenant');
          // No redirigir en páginas públicas (privacidad, términos, login…) para
          // que un 401 de fondo no patee al visitante fuera de la página.
          const p = window.location.pathname;
          const isPublic = p === '/privacidad' || p === '/soporte' || p === '/terminos' || p === '/login'
            || p === '/register' || p === '/signup-wa' || p === '/forgot-password' || p === '/reset-password'
            || p === '/promo' || p.startsWith('/trace/');
          if (!isPublic) {
            window.location.href = '/login';
          }
        }

        // 402 Payment Required — trial expiró o suscripción suspendida.
        // El backend retorna { code: "TRIAL_EXPIRED" | "SUBSCRIPTION_SUSPENDED", redirectTo }.
        if (error.response?.status === 402) {
          const code = error.response.data?.code;
          const redirectTo = error.response.data?.redirectTo || '/subscription/upgrade';
          if ((code === 'TRIAL_EXPIRED' || code === 'SUBSCRIPTION_SUSPENDED') &&
              !window.location.pathname.startsWith('/subscription')) {
            window.location.href = redirectTo;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.api.post<AuthResponse>('/auth/login', credentials);
    return response.data;
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await this.api.post<AuthResponse>('/auth/register', userData);
    return response.data;
  }

  async googleAuth(data: GoogleAuthRequest): Promise<AuthResponse> {
    const response = await this.api.post<AuthResponse>('/auth/google', data);
    return response.data;
  }

  async validateToken(token: string): Promise<{ valid: boolean }> {
    const response = await this.api.post<{ valid: boolean }>('/auth/validate-token', token);
    return response.data;
  }

  async forgotPassword(email: string): Promise<{ message: string; token?: string }> {
    const response = await this.api.post<{ message: string; token?: string }>('/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const response = await this.api.post<{ message: string }>('/auth/reset-password', { token, newPassword });
    return response.data;
  }

  // WhatsApp OTP (alta/login passwordless)
  async phoneStart(payload: { phone: string; utmSource?: string; utmMedium?: string; utmCampaign?: string }): Promise<{ success: boolean; message?: string; isExisting?: boolean; devCode?: string | null }> {
    const response = await this.api.post('/registration/phone/start', payload);
    return response.data;
  }

  async phoneVerify(payload: { phone: string; code: string; utmSource?: string; utmMedium?: string; utmCampaign?: string; couponCode?: string }): Promise<AuthResponse & { isExisting: boolean }> {
    const response = await this.api.post('/registration/phone/verify', payload);
    // El backend devuelve { success, isExisting, auth: AuthResponse }. isExisting = login (no alta).
    return { ...(response.data.auth as AuthResponse), isExisting: Boolean(response.data.isExisting) };
  }

  // Product endpoints
  async getProducts(params: PaginationParams): Promise<ProductListResponse> {
    const response = await this.api.get<ProductListResponse>('/products', { params });
    return response.data;
  }

  async getProduct(id: number): Promise<Product> {
    const response = await this.api.get<Product>(`/products/${id}`);
    return response.data;
  }

  async createProduct(product: CreateProductRequest): Promise<Product> {
    const response = await this.api.post<Product>('/products', product);
    return response.data;
  }

  async updateProduct(id: number, product: Partial<CreateProductRequest>): Promise<Product> {
    const response = await this.api.put<Product>(`/products/${id}`, product);
    return response.data;
  }

  async deleteProduct(id: number): Promise<void> {
    await this.api.delete(`/products/${id}`);
  }

  async getProductStats(): Promise<ProductStats> {
    const response = await this.api.get<ProductStats>('/products/stats');
    return response.data;
  }

  // Product variants (talles / colores con stock propio)
  async getProductVariants(productId: number): Promise<ProductVariant[]> {
    const response = await this.api.get(`/products/${productId}/variants`);
    return (response.data || []) as ProductVariant[];
  }

  async setProductVariants(productId: number, variants: ProductVariant[]): Promise<void> {
    await this.api.put(`/products/${productId}/variants`, variants);
  }

  async getProductTraceability(productId: number): Promise<ProductTraceability> {
    const response = await this.api.get(`/products/${productId}/traceability`);
    return response.data as ProductTraceability;
  }

  async getPublicTrace(batchPublicId: string): Promise<{ productName: string; sku: string; batchPublicId: string; producedAt?: string; components: any[] }> {
    const response = await this.api.get(`/public/trace/${encodeURIComponent(batchPublicId)}`);
    return response.data;
  }

  // Product categories (tenant settings)
  async getCategories(): Promise<{ items: { id: number; name: string; isActive: boolean; sortOrder: number }[] }> {
    const response = await this.api.get('/settings/categories');
    const items = (response.data || []) as any[];
    return { items: items.map((c: any) => ({ id: c.id, name: c.name, isActive: !!c.isActive, sortOrder: c.sortOrder ?? 0 })) };
  }

  // Tenant settings
  async getTenantMe(): Promise<Tenant> {
    const res = await this.api.get<Tenant>('/tenants/me');
    return res.data as any;
  }

  async updateTenantMe(payload: { name?: string; logoUrl?: string; primaryColor?: string; secondaryColor?: string }): Promise<Tenant> {
    const res = await this.api.put<Tenant>('/tenants/me', payload);
    return res.data as any;
  }
  async getUsers(params?: { role?: string; includeInactive?: boolean }): Promise<{ items: User[] }> {
    const res = await this.api.get<{ items: User[] }>('/users', { params });
    return res.data;
  }
  async createUser(payload: { name: string; email: string; password: string; role: string }): Promise<User> {
    const res = await this.api.post<User>('/users', payload);
    return res.data;
  }
  async updateUser(id: number, payload: { name?: string; email?: string; role?: string; isActive?: boolean; password?: string }): Promise<User> {
    const res = await this.api.put<User>(`/users/${id}`, payload);
    return res.data;
  }
  async deleteUser(id: number): Promise<void> {
    await this.api.delete(`/users/${id}`);
  }
  async setMyPassword(password: string): Promise<void> {
    await this.api.post('/users/me/password', { password });
  }

  // ===== IA (Plan IA) =====
  async aiScanInvoice(file: File): Promise<{ invoice: any; suggestedProducts?: any[] | null }> {
    const fd = new FormData();
    fd.append('file', file);
    const res = await this.api.post('/ai/invoice-scan', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data;
  }
  async aiDeadStock(days = 60): Promise<{ days: number; items: any[] }> {
    const res = await this.api.get('/ai/dead-stock', { params: { days } });
    return res.data;
  }
  async aiPricingSuggestions(targetMarginPct = 40): Promise<{ targetMarginPct: number; items: any[] }> {
    const res = await this.api.get('/ai/pricing-suggestions', { params: { targetMarginPct } });
    return res.data;
  }
  async createCategory(payload: { name: string; isActive?: boolean; sortOrder?: number }): Promise<{ id: number; name: string; isActive: boolean; sortOrder: number }> {
    const response = await this.api.post('/settings/categories', payload);
    return response.data;
  }
  async updateCategory(id: number, payload: { name: string; isActive?: boolean; sortOrder?: number }): Promise<{ id: number; name: string; isActive: boolean; sortOrder: number }> {
    const response = await this.api.put(`/settings/categories/${id}`, payload);
    return response.data;
  }
  async deleteCategory(id: number): Promise<void> {
    await this.api.delete(`/settings/categories/${id}`);
  }
  async downloadCategoriesCsv(): Promise<Blob> {
    const response = await this.api.get('/settings/categories/export.csv', { responseType: 'blob' });
    return response.data as Blob;
  }
  async bulkImportCategories(items: { name: string; sortOrder?: number; isActive?: boolean }[]): Promise<{ created: number; updated: number; skipped: number }> {
    const response = await this.api.post('/settings/categories/import', { items });
    return response.data;
  }

  async bulkImportProducts(items: any[]): Promise<{ created: number; updated: number; skipped: number; errors: string[] }> {
    const response = await this.api.post('/products/bulk', { items });
    return response.data;
  }

  async downloadProductsCsv(): Promise<Blob> {
    const response = await this.api.get('/products/export.csv', { responseType: 'blob' });
    return response.data as Blob;
  }

  async downloadSuppliersCsv(): Promise<Blob> {
    const response = await this.api.get('/suppliers/export.csv', { responseType: 'blob' });
    return response.data as Blob;
  }

  async downloadMovementsCsv(params: { productId?: number }): Promise<Blob> {
    const response = await this.api.get('/inventory/movements/export.csv', { params, responseType: 'blob' });
    return response.data as Blob;
  }

  async getLocations(): Promise<{ locations: string[] }> {
    const response = await this.api.get('/inventory/locations');
    return response.data;
  }

  async getLocationStocks(productId: number): Promise<{ items: { location: string; stock: number }[] }> {
    const response = await this.api.get('/inventory/location-stocks', { params: { productId } });
    return response.data;
  }

  async transferInternal(payload: { productId: number; fromLocation: string; toLocation: string; quantity: number; reason?: string; referenceNumber?: string; notes?: string }): Promise<{ success: boolean }> {
    const response = await this.api.post('/inventory/transfers/internal', payload);
    return response.data;
  }

  // Channels
  async getChannels(): Promise<{ items: { id: number; name: string; type: string; isActive: boolean; isConnected: boolean; lastSyncAt?: string; syncStatus?: string; lastSyncError?: string; autoSyncEnabled?: boolean; autoPushStock?: boolean; stockAdjustFrom?: string | null; ordersSyncedUntil?: string | null; lastAutoSyncAt?: string | null; lastSyncOrders?: number; historyBackfilledAt?: string | null; historyBackfillNote?: string | null }[] }> {
    const response = await this.api.get('/channels');
    return response.data;
  }

  async connectMercadoLibre(channelId: number): Promise<{ success: boolean }> {
    const response = await this.api.post('/channels/ml/connect', { channelId });
    return response.data;
  }

  async getMercadoLibreOAuthUrl(channelId: number): Promise<{ url: string }> {
    const response = await this.api.get('/channels/ml/oauth/start', { params: { channelId } });
    return response.data;
  }

  async getTiendaNubeOAuthUrl(channelId: number): Promise<{ url: string }> {
    const response = await this.api.get('/channels/tn/oauth/start', { params: { channelId } });
    return response.data;
  }

  async getMlPublications(channelId: number, params?: { limit?: number; offset?: number; fetchAll?: boolean }): Promise<{ items: any[]; paging: { limit: number; offset: number; total: number } }> {
    const response = await this.api.get('/channels/ml/publications', { params: { channelId, ...(params || {}) } });
    return response.data;
  }

  async mapMlPublication(payload: { channelId: number; externalId: string; userProductId?: string; relinkPast?: boolean; mappings: { productId: number; externalSku?: string; variationId?: string; mlStoreId?: string; mlNetworkNodeId?: string; lastStockVersion?: number; quantity?: number }[] }): Promise<{ success: boolean; relinked?: number }> {
    const response = await this.api.post('/channels/ml/publications/map', payload);
    return response.data;
  }

  // Resincronizar todo con revisión: nada se graba hasta aprobar cada diferencia.
  async connectShopify(dto: { shop: string; clientId: string; clientSecret: string }): Promise<{ id: number; shop: string; shopName?: string; scope?: string; message: string }> {
    return (await this.api.post('/channels/shopify/connect', dto)).data;
  }
  async startResync(channelId: number): Promise<ResyncPreview> { return (await this.api.post(`/channels/${channelId}/resync`, {})).data; }
  async getResync(channelId: number, id: number): Promise<ResyncPreview> { return (await this.api.get(`/channels/${channelId}/resync/${id}`)).data; }
  async applyResync(channelId: number, id: number, acceptExternalIds: string[]): Promise<ResyncPreview> {
    return (await this.api.post(`/channels/${channelId}/resync/${id}/apply`, { acceptExternalIds })).data;
  }

  async requestHistoryBackfill(id: number): Promise<{ message: string }> {
    return (await this.api.post(`/channels/${id}/history-backfill`, {})).data;
  }

  async updateChannelSyncSettings(id: number, settings: { autoSyncEnabled: boolean; autoPushStock: boolean }): Promise<void> {
    await this.api.put(`/channels/${id}/sync-settings`, settings);
  }

  async syncChannel(id: number): Promise<{ success: boolean; lastSyncAt: string; ordersImported?: string[]; logs?: string[]; error?: string | null; processed?: number; failed?: number }> {
    const response = await this.api.post(`/channels/${id}/sync`, {});
    return response.data;
  }

  async syncChannelFullStock(id: number): Promise<{ success: boolean; lastSyncAt?: string; logs?: string[] }> {
    const response = await this.api.post(`/channels/${id}/full-stock-sync`, {});
    return response.data;
  }

  async disconnectChannel(id: number): Promise<{ success: boolean; message?: string }> {
    const response = await this.api.post(`/channels/${id}/disconnect`, {});
    return response.data;
  }

  // Stats
  async getSalesKpis(period: 'day' | 'week' | 'month'): Promise<{ period: string; totalSales: number; totalOrders: number; avgOrderValue: number; salesTrendPct: number; totalUnits: number }> {
    const response = await this.api.get('/stats/sales-kpis', { params: { period } });
    return response.data;
  }

  /** Rentabilidad del período (from/to yyyy-mm-dd, días de Argentina). */
  async getProfitability(params: { from?: string; to?: string; allocation?: 'revenue' | 'units' } = {}): Promise<Profitability> {
    return (await this.api.get('/stats/profitability', { params })).data;
  }

  async getSalesSeries(weeks = 12): Promise<{ items: { week: string; value: number }[] }> {
    const response = await this.api.get('/stats/sales-series', { params: { weeks } });
    return response.data;
  }

  // CRM: Customers
  async getCustomer(id: number): Promise<any> {
    const res = await this.api.get(`/customers/${id}`);
    return res.data;
  }

  async getCustomers(params: { page?: number; pageSize?: number; search?: string; type?: string; status?: string }): Promise<{ customers: any[]; totalCount: number; page: number; pageSize: number; totalPages: number }>{
    const res = await this.api.get('/customers', { params });
    const d = res.data as any;
    return {
      customers: d.customers || d.Customers || [],
      totalCount: d.totalCount || d.TotalCount || 0,
      page: d.page || d.Page || 1,
      pageSize: d.pageSize || d.PageSize || 20,
      totalPages: d.totalPages || d.TotalPages || 1,
    };
  }

  async createCustomer(payload: any): Promise<any> {
    const res = await this.api.post('/customers', payload);
    return res.data;
  }

  async updateCustomer(id: number, payload: any): Promise<any> {
    const res = await this.api.put(`/customers/${id}`, payload);
    return res.data;
  }

  async deleteCustomer(id: number): Promise<void> {
    await this.api.delete(`/customers/${id}`);
  }

  async getCustomerAddresses(customerId: number): Promise<{ items: any[] }>{
    const res = await this.api.get(`/customers/${customerId}/addresses`);
    return res.data;
  }

  async upsertCustomerAddress(customerId: number, address: any, addressId?: number): Promise<any> {
    if (addressId) {
      const res = await this.api.put(`/customers/${customerId}/addresses/${addressId}`, address);
      return res.data;
    }
    const res = await this.api.post(`/customers/${customerId}/addresses`, address);
    return res.data;
  }

  async getTopProducts(params: { days?: number; limit?: number }): Promise<{ since: string; days: number; topSeller?: any; topMargin?: any; topByUnits: any[]; topByMargin: any[] }>{
    const response = await this.api.get('/stats/top-products', { params });
    return response.data;
  }

  async getTopProductsRanking(params: { period?: string; limit?: number }): Promise<{ period: string; start: string; end: string; totalUnits: number; totalRevenue: number; items: Array<{ productId: number; productName: string; sku?: string; units: number; revenue: number; orders: number }> }>{
    const response = await this.api.get('/stats/top-products-ranking', { params });
    return response.data;
  }

  // Seller activities
  async listSellerActivities(params: { from?: string; to?: string; seller?: string; customerId?: number }): Promise<{ items: any[] }>{
    const res = await this.api.get('/sales-activities', { params });
    return res.data;
  }
  async createSellerActivity(payload: { customerId?: number; sellerName: string; channel: string; result?: string; nextStep?: string; nextStepAt?: string; activityAt?: string; notes?: string }): Promise<any>{
    const res = await this.api.post('/sales-activities', payload);
    return res.data;
  }
  async getSellerReport(params: { seller?: string; from?: string; to?: string }): Promise<{ from: string; to: string; items: any[] }>{
    const res = await this.api.get('/sales-activities/report', { params });
    return res.data;
  }
  async getPendingNextSteps(params: { until?: string }): Promise<{ items: any[] }>{
    const res = await this.api.get('/sales-activities/pending-next-steps', { params });
    return res.data;
  }

  // Opportunities
  async listOpportunities(params: { state?: string; owner?: string; month?: string; type?: string }): Promise<{ items: import('../types').Opportunity[] }>{
    const res = await this.api.get('/opportunities', { params });
    return res.data;
  }
  async createOpportunity(payload: any): Promise<any>{
    const res = await this.api.post('/opportunities', payload);
    return res.data;
  }
  async updateOpportunity(id: number, payload: any): Promise<any>{
    const res = await this.api.put(`/opportunities/${id}`, payload);
    return res.data;
  }
  async changeOpportunityState(id: number, state: string): Promise<{ success: boolean; state: string }>{
    const res = await this.api.post(`/opportunities/${id}/state`, { state });
    return res.data;
  }
  async getOpportunityProjection(month?: string): Promise<{ month: string; wonSum: number; projectedSum: number; expectedValue: number }>{
    const res = await this.api.get('/opportunities/projection', { params: { month } });
    return res.data;
  }
  async convertOpportunityToOrder(id: number): Promise<{ success: boolean; orderId: number; message: string }>{
    const res = await this.api.post(`/opportunities/${id}/convert-to-order`);
    return res.data;
  }
  async getOpportunityItems(id: number): Promise<{ items: import('../types').OpportunityItem[] }>{
    const res = await this.api.get(`/opportunities/${id}/items`);
    return res.data;
  }
  async addOpportunityItem(oppId: number, item: { productId: number; quantity: number; unitPrice: number; discountPct?: number; ivaRate?: number }): Promise<import('../types').OpportunityItem>{
    const res = await this.api.post(`/opportunities/${oppId}/items`, item);
    return res.data;
  }
  async deleteOpportunity(id: number): Promise<void>{
    await this.api.delete(`/opportunities/${id}`);
  }
  async removeOpportunityItem(oppId: number, itemId: number): Promise<void>{
    await this.api.delete(`/opportunities/${oppId}/items/${itemId}`);
  }
  async uploadOpportunityFile(id: number, payload: { fileType: string; fileName: string; base64: string }): Promise<{ success: boolean; url: string }>{
    const res = await this.api.post(`/opportunities/${id}/upload-file`, payload);
    return res.data;
  }
  async downloadOpportunityFile(id: number, fileType: string): Promise<Blob>{
    const res = await this.api.get(`/opportunities/${id}/file/${fileType}`, { responseType: 'blob' });
    return res.data;
  }
  async deleteOpportunityFile(id: number, fileType: string): Promise<void>{
    await this.api.delete(`/opportunities/${id}/file/${fileType}`);
  }
  async uploadOrderFile(id: number, payload: { fileType: string; fileName: string; base64: string }): Promise<{ success: boolean; url: string }>{
    const res = await this.api.post(`/orders/${id}/upload-file`, payload);
    return res.data;
  }
  async downloadOrderFile(id: number, fileType: string): Promise<Blob>{
    const res = await this.api.get(`/orders/${id}/file/${fileType}`, { responseType: 'blob' });
    return res.data;
  }
  async deleteOrderFile(id: number, fileType: string): Promise<void>{
    await this.api.delete(`/orders/${id}/file/${fileType}`);
  }

  // Prospects CRUD
  async getProspects(params: { page?: number; pageSize?: number; search?: string; status?: string; seller?: string }): Promise<{ items: import('../types').Prospect[]; totalCount: number; page: number; pageSize: number; totalPages: number }>{
    const res = await this.api.get('/prospects', { params });
    return res.data;
  }
  async createProspect(payload: Partial<import('../types').Prospect>): Promise<import('../types').Prospect>{
    const res = await this.api.post('/prospects', payload);
    return res.data;
  }
  async updateProspect(id: number, payload: Partial<import('../types').Prospect>): Promise<import('../types').Prospect>{
    const res = await this.api.put(`/prospects/${id}`, payload);
    return res.data;
  }
  async deleteProspect(id: number): Promise<void>{
    await this.api.delete(`/prospects/${id}`);
  }
  async convertProspectToCustomer(id: number): Promise<{ message: string; customerId: number; customerName: string }>{
    const res = await this.api.post(`/prospects/${id}/convert`);
    return res.data;
  }

  // Reports - Forecast Dashboard
  async getForecastDashboard(params?: { year?: number; month?: number }): Promise<import('../types').ForecastDashboard>{
    const res = await this.api.get('/stats/forecast-dashboard', { params });
    return res.data;
  }
  async getMonthlyOperationsChart(months?: number): Promise<{ items: { month: string; count: number }[] }>{
    const res = await this.api.get('/stats/monthly-operations-chart', { params: { months } });
    return res.data;
  }
  async getMonthlyBillingChart(months?: number): Promise<{ items: { month: string; total: number }[] }>{
    const res = await this.api.get('/stats/monthly-billing-chart', { params: { months } });
    return res.data;
  }
  async getQuarterlyChart(quartersBack?: number): Promise<{ items: { quarter: string; actual: number; estimated: number }[] }>{
    const res = await this.api.get('/stats/quarterly-chart', { params: { quartersBack } });
    return res.data;
  }
  async getSalesByTypeChart(months?: number): Promise<{ items: { month: string; type: string; total: number }[] }>{
    const res = await this.api.get('/stats/sales-by-type-chart', { params: { months } });
    return res.data;
  }
  async getCustomerPieQuarter(): Promise<{ period: string; items: { customer: string; total: number }[] }>{
    const res = await this.api.get('/stats/customer-pie-quarter');
    return res.data;
  }
  async getCustomerPieYear(): Promise<{ year: number; items: { customer: string; total: number }[] }>{
    const res = await this.api.get('/stats/customer-pie-year');
    return res.data;
  }
  async getCashFlow(params?: { from?: string; to?: string }): Promise<import('../types').CashFlowResponse>{
    const res = await this.api.get('/stats/cash-flow', { params });
    return res.data;
  }

  // Inventory endpoints
  async adjustStock(productId: number, newStock: number, reason: string, notes?: string, lotNumber?: string, expirationDate?: string, batchAllocations?: { batchId: number; quantity: number }[]): Promise<void> {
    await this.api.post('/inventory/adjust', {
      productId,
      newStock,
      reason,
      notes,
      lotNumber: lotNumber || null,
      expirationDate: expirationDate || null,
      batchAllocations: batchAllocations && batchAllocations.length ? batchAllocations : null,
    });
  }

  async adjustMaterialStock(payload: { materialId: number; type: 'IN' | 'OUT'; quantity: number; reason: string; notes?: string; lotNumber?: string; expirationDate?: string; supplierId?: number }): Promise<{ success: boolean; newStock: number }> {
    const res = await this.api.post('/inventory/adjust-material', {
      ...payload,
      lotNumber: payload.lotNumber || null,
      expirationDate: payload.expirationDate || null,
      supplierId: payload.supplierId ?? null,
    });
    return res.data;
  }

  async getInventoryMovements(params: { page?: number; pageSize?: number; productId?: number; type?: string; reason?: string; from?: string; to?: string }): Promise<{ items: any[]; totalCount: number; page: number; pageSize: number; totalPages: number }> {
    const response = await this.api.get('/inventory/movements', { params });
    return response.data;
  }

  async getMaterialMovements(params: { page?: number; pageSize?: number; materialId?: number; type?: string; reason?: string; from?: string; to?: string }): Promise<{ items: any[]; totalCount: number; page: number; pageSize: number; totalPages: number }> {
    const response = await this.api.get('/inventory/material-movements', { params });
    return response.data;
  }

  async downloadMaterialMovementsCsv(params: { materialId?: number; type?: string; reason?: string; from?: string; to?: string }): Promise<Blob> {
    const response = await this.api.get('/inventory/material-movements/export.csv', { params, responseType: 'blob' });
    return response.data as Blob;
  }

  async createShipmentToMlFull(payload: { productId: number; quantity: number; referenceNumber?: string; notes?: string }): Promise<{ success: boolean; productId: number; newStock: number; mlFullStock?: number }> {
    const response = await this.api.post('/inventory/shipments/ml-full', payload);
    return response.data;
  }

  async getMlFullShipments(params: { page?: number; pageSize?: number; fromDate?: string; toDate?: string }): Promise<{ items: any[]; totalCount: number; page: number; pageSize: number; totalPages: number; mlHasMore?: boolean }> {
    const response = await this.api.get('/inventory/shipments/ml-full', { params });
    return response.data;
  }

  // Warehouses
  async getWarehouses(): Promise<{ items: Warehouse[] }> {
    const res = await this.api.get('/warehouses');
    return res.data;
  }

  async createWarehouse(payload: { name: string; code?: string; type?: string; address?: string; notes?: string }): Promise<Warehouse> {
    const res = await this.api.post('/warehouses', payload);
    return res.data;
  }

  async updateWarehouse(id: number, payload: { name: string; code?: string; type?: string; address?: string; notes?: string; isActive?: boolean }): Promise<Warehouse> {
    const res = await this.api.put(`/warehouses/${id}`, payload);
    return res.data;
  }

  async deleteWarehouse(id: number): Promise<void> {
    await this.api.delete(`/warehouses/${id}`);
  }

  // Sales
  async registerSale(payload: RegisterSaleRequest): Promise<RegisterSaleResponse> {
    const response = await this.api.post<RegisterSaleResponse>('/sales', payload);
    return response.data;
  }

  async getSalesFeed(params: { page?: number; pageSize?: number }): Promise<SaleFeedResponse> {
    const response = await this.api.get('/sales/feed', { params });
    return (response.data?.data as SaleFeedResponse) || { items: [], totalCount: 0, page: 1, pageSize: 20, totalPages: 1 };
  }

  // Orders
  async getOrders(params: { page?: number; pageSize?: number; status?: string; search?: string; from?: string; to?: string; reference?: string; channel?: string; customer?: string; minTotal?: number; maxTotal?: number; minUnits?: number; maxUnits?: number }): Promise<OrderListResponse> {
    const response = await this.api.get('/orders', { params });
    const d = response.data as any;
    return {
      items: (d.items || d.Items || []).map((it: any) => ({ ...it, units: it.units ?? it.Units })),
      totalCount: d.totalCount || d.TotalCount || 0,
      page: d.page || d.Page || params.page || 1,
      pageSize: d.pageSize || d.PageSize || params.pageSize || 20,
      totalPages: d.totalPages || d.TotalPages || 1,
    };
  }

  async getOrder(id: number): Promise<OrderDetail> {
    const response = await this.api.get(`/orders/${id}`);
    return response.data as OrderDetail;
  }

  async updateOrder(id: number, payload: Record<string, any>): Promise<OrderDetail> {
    const response = await this.api.put(`/orders/${id}`, payload);
    return response.data as OrderDetail;
  }

  // Edita solo el título de la venta; funciona también en ventas ya concretadas.
  async updateOrderName(id: number, name: string | null): Promise<{ name: string | null }> {
    const response = await this.api.put(`/orders/${id}/name`, { name });
    return response.data as { name: string | null };
  }

  async revertOrderDelivery(id: number): Promise<OrderDetail> {
    const response = await this.api.post(`/orders/${id}/revert-delivery`);
    return response.data as OrderDetail;
  }

  async deleteOrder(id: number): Promise<void> {
    await this.api.delete(`/orders/${id}`);
  }

  async bulkMarkOrdersDelivered(cutoffDate: string): Promise<{ updated: number }> {
    const response = await this.api.post('/orders/bulk-mark-delivered', { cutoffDate });
    return response.data as { updated: number };
  }

  async registerOrderPayment(
    id: number,
    payload: { amount: number; paymentDate?: string; paymentMethod?: string; reference?: string; notes?: string }
  ): Promise<{ success: boolean; paidAmount: number; balance: number; paymentStatus: string }> {
    const response = await this.api.post(`/orders/${id}/payments`, payload);
    return response.data;
  }

  async getFeatureFlags(): Promise<FeatureFlag[]> {
    const response = await this.api.get('/feature-flags');
    // API returns ApiResponseDto<T>
    return (response.data?.data as FeatureFlag[]) || [];
  }

  async getCustomerInactivityAlerts(params: { days?: number; top?: number }): Promise<{ items: CustomerInactivityAlert[]; generatedAt: string }> {
    const response = await this.api.get('/customers/inactivity-alerts', { params });
    return response.data || { items: [], generatedAt: new Date().toISOString() };
  }

  async getCustomerAnalytics(customerId: number): Promise<CustomerAnalytics> {
    const response = await this.api.get(`/customers/${customerId}/analytics`);
    return response.data;
  }

  async getSalesByChannel(params?: { from?: string; to?: string; period?: 'day' | 'week' | 'month' }): Promise<{ from: string; to: string; totalSales: number; totalUnits: number; totalOrders: number; items: { channel: string; totalSales: number; totalUnits: number; totalOrders: number }[] }> {
    const response = await this.api.get('/stats/sales-by-channel', { params });
    return response.data;
  }

  async getProductSeasonality(params?: { from?: string; to?: string; productId?: number; top?: number }): Promise<ProductSeasonalityReport> {
    const response = await this.api.get('/stats/product-seasonality', { params });
    const data = response.data as any;
    return {
      from: data?.from,
      to: data?.to,
      summary: data?.summary || [],
      items: data?.items || [],
    };
  }

  // Suppliers
  async getSuppliers(params: { page?: number; pageSize?: number; search?: string; type?: string }): Promise<{ suppliers: any[]; totalCount: number; page: number; pageSize: number; totalPages: number }> {
    const response = await this.api.get('/suppliers', { params });
    return response.data;
  }

  async createSupplier(data: any): Promise<any> {
    const response = await this.api.post('/suppliers', data);
    return response.data;
  }

  // Recipes (BOM)
  async getRecipes(params: { page?: number; pageSize?: number; search?: string; productId?: number }): Promise<RecipeListResponse> {
    const response = await this.api.get<RecipeListResponse>('/recipes', { params });
    return response.data;
  }

  async getRecipe(id: number): Promise<Recipe> {
    const response = await this.api.get<Recipe>(`/recipes/${id}`);
    return response.data;
  }

  async createRecipe(payload: CreateRecipeRequest): Promise<Recipe> {
    const response = await this.api.post<Recipe>('/recipes', payload);
    return response.data;
  }

  async updateRecipe(id: number, payload: Partial<CreateRecipeRequest>): Promise<Recipe> {
    const response = await this.api.put<Recipe>(`/recipes/${id}`, payload);
    return response.data;
  }

  async deleteRecipe(id: number): Promise<void> {
    await this.api.delete(`/recipes/${id}`);
  }

  // Recipe operations (process + dependencies)
  async getRecipeOperations(recipeId: number): Promise<Array<{ id: number; name: string; serviceId?: number; durationHours: number; order?: number; dependsOn: number[] }>> {
    const res = await this.api.get(`/recipes/${recipeId}/operations`);
    return res.data || [];
  }
  async upsertRecipeOperations(recipeId: number, operations: Array<{ id: number; name: string; serviceId?: number; durationHours: number; order?: number; dependsOn: number[] }>): Promise<void> {
    await this.api.put(`/recipes/${recipeId}/operations`, { operations });
  }
  async getRecipeSchedule(recipeId: number): Promise<{ recipeId: number; tasks: Array<{ id: number; name: string; durationDays: number; startDay: number; endDay: number; isCritical: boolean }>; totalDurationDays: number }>{
    const res = await this.api.get(`/recipes/${recipeId}/operations/schedule`);
    return res.data;
  }
  async getRecipeCost(recipeId: number, quantity: number): Promise<{ recipeId: number; productId: number; productName?: string; quantity: number; unitCostMaterials: number; unitCostServices: number; unitCostTotal: number; productPrice?: number; grossMargin?: number; grossMarginPct?: number; materials: any[]; services: any[] }>{
    const res = await this.api.get(`/recipes/${recipeId}/cost`, { params: { quantity } });
    return res.data;
  }

  // Production orders
  async hideProductionOrder(id: number): Promise<void> {
    await this.api.post(`/production-orders/${id}/hide`, {});
  }

  async unhideProductionOrder(id: number): Promise<void> {
    await this.api.post(`/production-orders/${id}/unhide`, {});
  }

  async hideCompletedProductionOrders(): Promise<{ hidden: number }> {
    const res = await this.api.post('/production-orders/hide-completed', {});
    return res.data;
  }

  async getProductionOrders(params?: { status?: string; includeHidden?: boolean }): Promise<{ items: ProductionOrder[]; totalCount: number }>{
    const res = await this.api.get('/production-orders', { params });
    const data = res.data as any;
    return {
      items: data.items || data.Items || [],
      totalCount: data.totalCount || data.TotalCount || 0,
    };
  }

  async createProductionOrder(payload: { productId: number; recipeId: number; quantity: number; scheduledDate?: string; notes?: string; lotCode?: string; expirationDate?: string }): Promise<ProductionOrder> {
    const res = await this.api.post<ProductionOrder>('/production-orders', payload);
    return res.data;
  }

  async startProductionOrder(id: number): Promise<void> {
    await this.api.post(`/production-orders/${id}/start`, {});
  }

  async completeProductionOrder(id: number, body?: {
    manualAllocations?: Array<{ materialId: number; batchAllocations: Array<{ batchId: number; quantity: number }>; untrackedQuantity?: number }>;
    manualProductAllocations?: Array<{ productId: number; batchAllocations: Array<{ batchId: number; quantity: number }> }>;
    lotCode?: string;
    expirationDate?: string;
    actualQuantity?: number;
    yieldVarianceReason?: string;
    retentionQuantity?: number;
    analysisQuantity?: number;
  }): Promise<void> {
    await this.api.post(`/production-orders/${id}/complete`, body || {});
  }

  async getProposedConsumption(id: number): Promise<{
    materials: Array<{ materialId: number; materialName: string; quantityRequired: number; untrackedAvailable: number; batches: Array<{ batchId: number; publicId?: string | null; availableQuantity: number; proposedQuantity: number; unitCost: number; expirationDate?: string | null }> }>;
    products: Array<{ productId: number; productName: string; quantityRequired: number; batches: Array<{ batchId: number; publicId?: string | null; availableQuantity: number; proposedQuantity: number; unitCost: number; expirationDate?: string | null }> }>;
  }> {
    const res = await this.api.get(`/production-orders/${id}/proposed-consumption`);
    return res.data;
  }

  // Listado de solo lectura de muestras de retención/análisis (alta automática al
  // completar una orden de producción).
  async getRetentionSamples(filters?: { productId?: number; productionOrderId?: number; from?: string; to?: string }): Promise<{ items: RetentionSample[]; totalCount: number }> {
    const res = await this.api.get('/retention-samples', { params: filters });
    return res.data;
  }

  // Alta manual de muestra de retención/análisis — solo para el caso borde de un
  // lote ya completado sin muestra registrada, que la necesita antes de poder
  // liberarse (restringido a Calidad/Dirección Técnica en el backend).
  async createRetentionSample(payload: { inventoryBatchId: number; quantityRetention: number; quantityAnalysis: number; notes?: string }): Promise<RetentionSample> {
    const res = await this.api.post('/retention-samples', payload);
    return res.data;
  }

  async getMaterialBatches(params?: { materialId?: number; expirationFrom?: string; expirationTo?: string; productionOrderId?: number; onlyAvailable?: boolean; search?: string }): Promise<{ items: Array<{ id: number; publicId?: string | null; lotCode?: string | null; materialId: number; materialName?: string | null; quantity: number; unitCost: number; expirationDate?: string | null; purchaseReference?: string | null; supplierId?: number | null; supplierName?: string | null; createdAt: string; consumedBy: Array<{ productionOrderId: number; quantityUsed: number; createdAt: string }> }> }> {
    const res = await this.api.get('/material-batches', { params });
    return res.data;
  }

  async deleteMaterialBatch(id: number): Promise<void> {
    await this.api.delete(`/material-batches/${id}`);
  }

  // Ajuste manual de la cantidad de un lote de materia prima (motivo obligatorio).
  async adjustMaterialBatch(id: number, payload: { newQuantity: number; reason: string; notes?: string; updateSupplier?: boolean; supplierId?: number | null }): Promise<{ success: boolean; quantity: number; currentStock: number; supplierId?: number | null }> {
    const res = await this.api.post(`/material-batches/${id}/adjust`, payload);
    return res.data;
  }

  async updateMaterialBatch(id: number, payload: { lotCode?: string | null; expirationDate?: string | null }): Promise<{ success: boolean }> {
    const res = await this.api.put(`/material-batches/${id}`, payload);
    return res.data;
  }

  async updateProductBatch(id: number, payload: { batchCode?: string | null; expirationDate?: string | null }): Promise<{ success: boolean }> {
    const res = await this.api.put(`/inventory/product-batches/${id}`, payload);
    return res.data;
  }

  // Liberación a venta de un lote de producto terminado (control de calidad).
  async releaseProductBatch(id: number): Promise<{ success: boolean; isReleased: boolean; releasedAt: string }> {
    const res = await this.api.post(`/inventory/product-batches/${id}/release`, {});
    return res.data;
  }

  async cancelProductionOrder(id: number): Promise<void> {
    await this.api.post(`/production-orders/${id}/cancel`, {});
  }

  // Reintenta reservar stock para una OP "Pausada" por falta de material. Si ahora
  // alcanza, descuenta stock y pasa a Planificada; si no, tira 400 con el detalle.
  async retryProductionOrderStock(id: number): Promise<ProductionOrder> {
    const res = await this.api.post<ProductionOrder>(`/production-orders/${id}/retry-stock`, {});
    return res.data;
  }

  // Pausa una OP en curso como "Semielaborado" (producción física avanzada/terminada,
  // pendiente de control de calidad/etiquetado). No toca stock ni reservas.
  async pauseSemielaboradoOrder(id: number, progressNote?: string): Promise<void> {
    await this.api.post(`/production-orders/${id}/pause-semielaborado`, { progressNote });
  }

  // Desarrollos/pruebas de producto (pestaña aparte dentro de Producción, pedido de
  // Micaela): descuentan materia prima sin lote ni orden de producción.
  async getDevelopments(params?: { customerId?: number }): Promise<{ items: Development[]; totalCount: number }> {
    const res = await this.api.get('/developments', { params });
    return res.data;
  }

  async createDevelopment(payload: {
    name: string;
    customerId: number;
    notes?: string;
    elaborationDate?: string;
    sampleNotes?: string;
    materialUsages: { materialId: number; quantity: number }[];
  }): Promise<Development> {
    const res = await this.api.post<Development>('/developments', payload);
    return res.data;
  }

  async addDevelopmentSample(developmentId: number, payload: {
    elaborationDate?: string;
    notes?: string;
    materialUsages: { materialId: number; quantity: number }[];
  }): Promise<Development> {
    const res = await this.api.post<Development>(`/developments/${developmentId}/samples`, payload);
    return res.data;
  }

  async deleteDevelopmentSample(sampleId: number): Promise<void> {
    await this.api.delete(`/developments/samples/${sampleId}`);
  }

  async deleteDevelopment(id: number): Promise<void> {
    await this.api.delete(`/developments/${id}`);
  }

  async updateProductionOrder(id: number, payload: { orderNumber?: string; quantity?: number; recipeId?: number; scheduledDate?: string; notes?: string; lotCode?: string; expirationDate?: string }): Promise<ProductionOrder> {
    const res = await this.api.put<ProductionOrder>(`/production-orders/${id}`, payload);
    return res.data;
  }

  async deleteProductionOrder(id: number): Promise<void> {
    await this.api.delete(`/production-orders/${id}`);
  }

  // Materials
  async getMaterials(params: { page?: number; pageSize?: number; search?: string }): Promise<{ materials: Material[]; totalCount: number; page: number; pageSize: number; totalPages: number }> {
    const response = await this.api.get('/materials', { params });
    const data = response.data as any;
    // Normalize for frontend naming
    return {
      materials: data.materials || data.Materials || [],
      totalCount: data.totalCount || data.TotalCount || 0,
      page: data.page || data.Page || 1,
      pageSize: data.pageSize || data.PageSize || 50,
      totalPages: data.totalPages || data.TotalPages || 1,
    };
  }

  async createMaterial(payload: Partial<Material>): Promise<Material> {
    const response = await this.api.post('/materials', payload);
    return response.data;
  }

  async updateMaterial(id: number, payload: Partial<Material>): Promise<Material> {
    const response = await this.api.put(`/materials/${id}`, payload);
    return response.data;
  }

  async deleteMaterial(id: number): Promise<void> {
    await this.api.delete(`/materials/${id}`);
  }

  async createMaterialBatch(materialId: number, payload: { quantity: number; unitCost: number; purchaseReference?: string; supplierId?: number; expirationDate?: string | null }): Promise<any> {
    const response = await this.api.post(`/materials/${materialId}/batches`, payload);
    return response.data;
  }

  // Material suppliers lead times
  async getMaterialSupplierLeads(materialId: number): Promise<{ materialId: number; items: { supplierId: number; supplierName?: string; leadTimeDays: number }[] }> {
    const response = await this.api.get(`/materials/${materialId}/suppliers-lead`);
    return response.data;
  }

  async upsertMaterialSupplierLeads(materialId: number, items: { supplierId: number; leadTimeDays: number }[]): Promise<void> {
    await this.api.put(`/materials/${materialId}/suppliers-lead`, { materialId, items });
  }

  // Desglose del stock de una materia prima por proveedor (suma de lotes con stock).
  async getMaterialStockBySupplier(materialId: number): Promise<{ materialId: number; currentStock: number; items: { supplierId: number | null; supplierName: string; quantity: number; batchCount: number }[] }> {
    const response = await this.api.get(`/materials/${materialId}/stock-by-supplier`);
    return response.data;
  }

  // Services
  async getServices(params: { page?: number; pageSize?: number; search?: string }): Promise<{ services: Service[]; totalCount: number; page: number; pageSize: number; totalPages: number }> {
    const response = await this.api.get('/services', { params });
    const data = response.data as any;
    return {
      services: data.services || [],
      totalCount: data.totalCount || 0,
      page: data.page || 1,
      pageSize: data.pageSize || 50,
      totalPages: data.totalPages || 1,
    };
  }

  async createService(payload: Partial<Service>): Promise<Service> {
    const response = await this.api.post('/services', payload);
    return response.data;
  }

  async updateService(id: number, payload: Partial<Service>): Promise<Service> {
    const response = await this.api.put(`/services/${id}`, payload);
    return response.data;
  }

  async deleteService(id: number): Promise<void> {
    await this.api.delete(`/services/${id}`);
  }

  // Service pricing
  async getServicePricing(serviceId: number): Promise<import('../types').ServicePricing> {
    const res = await this.api.get(`/services/${serviceId}/pricing`);
    return res.data;
  }

  async upsertServicePricing(serviceId: number, payload: import('../types').ServicePricing): Promise<void> {
    await this.api.put(`/services/${serviceId}/pricing`, payload);
  }

  // Insumos
  async getInsumos(params: { page?: number; pageSize?: number; search?: string; isActive?: boolean }): Promise<import('../types').InsumoListResponse> {
    const response = await this.api.get('/insumos', { params });
    return response.data;
  }

  async getInsumo(id: number): Promise<import('../types').Insumo> {
    const response = await this.api.get(`/insumos/${id}`);
    return response.data;
  }

  async createInsumo(payload: import('../types').CreateInsumoRequest): Promise<import('../types').Insumo> {
    const response = await this.api.post('/insumos', payload);
    return response.data;
  }

  async updateInsumo(id: number, payload: Partial<import('../types').CreateInsumoRequest>): Promise<import('../types').Insumo> {
    const response = await this.api.put(`/insumos/${id}`, payload);
    return response.data;
  }

  async deleteInsumo(id: number): Promise<void> {
    await this.api.delete(`/insumos/${id}`);
  }

  async recalculateInsumoCost(id: number): Promise<import('../types').Insumo> {
    const response = await this.api.post(`/insumos/${id}/recalculate-cost`);
    return response.data;
  }

  async updateSupplier(id: number, data: any): Promise<any> {
    const response = await this.api.put(`/suppliers/${id}`, data);
    return response.data;
  }

  async deleteSupplier(id: number): Promise<void> {
    await this.api.delete(`/suppliers/${id}`);
  }

  // Purchases suggestions
  async getPurchaseSuggestions(): Promise<{ items: { productId: number; productName: string; sku: string; currentStock: number; minStock: number; reorderPoint: number; suggestedQty: number; supplierName?: string; supplierLeadTimeDays?: number; reason: string }[] }> {
    const response = await this.api.get('/purchases/suggestions');
    return response.data as any;
  }

  async getMaterialSuggestions(): Promise<{ items: { materialId: number; name: string; code?: string; currentStock: number; reorderPoint: number; suggestedQty: number; supplierName?: string; supplierLeadTimeDays?: number; reason: string }[] }> {
    const response = await this.api.get('/purchases/material-suggestions');
    return response.data as any;
  }

  // Purchases CRUD
  async getPurchases(): Promise<{ items: any[] }> {
    const response = await this.api.get('/purchases');
    return response.data as any;
  }

  async getPurchase(id: number): Promise<any> {
    const response = await this.api.get(`/purchases/${id}`);
    return response.data;
  }

  async createPurchase(payload: { reference: string; supplierId?: number; expectedDate?: string; requiredDate?: string; purchaseRequestId?: number; status?: string; currency?: string; exchangeRate?: number; total: number; notes?: string; paymentMethod?: string; paymentDueDate?: string; type?: string; installments?: any[]; items?: any[]; attachments?: { fileName: string; contentType: string; base64: string }[] }): Promise<any> {
    const response = await this.api.post('/purchases', payload);
    return response.data;
  }

  async updatePurchase(id: number, payload: { reference: string; supplierId?: number; expectedDate?: string; requiredDate?: string; status?: string; currency?: string; exchangeRate?: number; total: number; notes?: string; paymentMethod?: string; paymentDueDate?: string; type?: string; installments?: any[]; items?: any[]; attachments?: { fileName: string; contentType: string; base64: string }[] }): Promise<void> {
    await this.api.put(`/purchases/${id}`, payload);
  }

  async deletePurchase(id: number): Promise<void> {
    await this.api.delete(`/purchases/${id}`);
  }

  async registerPurchasePayment(
    id: number,
    payload: { amount: number; paymentDate?: string; paymentMethod?: string; reference?: string; notes?: string }
  ): Promise<any> {
    const response = await this.api.post(`/purchases/${id}/payment`, payload);
    return response.data;
  }

  async getPurchasePayments(params?: { page?: number; pageSize?: number }): Promise<{ items: any[]; totalCount: number; page: number; pageSize: number }> {
    const response = await this.api.get('/purchases/payments', { params });
    return response.data;
  }

  // PDF de la orden de compra (para mandar al proveedor)
  async downloadPurchaseOrderPdf(id: number): Promise<Blob> {
    const response = await this.api.get(`/purchases/${id}/pdf`, { responseType: 'blob' });
    return response.data as Blob;
  }

  // Pedidos internos de compra
  async getPurchaseRequests(status?: string): Promise<{ items: any[] }> {
    const response = await this.api.get('/purchase-requests', { params: status ? { status } : undefined });
    return response.data as any;
  }

  async getPurchaseRequest(id: number): Promise<any> {
    const response = await this.api.get(`/purchase-requests/${id}`);
    return response.data;
  }

  async createPurchaseRequest(payload: { priority?: string; requiredDate?: string; notes?: string; items: { productId?: number; materialId?: number; productName?: string; quantity: number; notes?: string }[] }): Promise<any> {
    const response = await this.api.post('/purchase-requests', payload);
    return response.data;
  }

  async updatePurchaseRequest(id: number, payload: { priority?: string; requiredDate?: string; notes?: string; items: { productId?: number; materialId?: number; productName?: string; quantity: number; notes?: string }[] }): Promise<void> {
    await this.api.put(`/purchase-requests/${id}`, payload);
  }

  async deletePurchaseRequest(id: number): Promise<void> {
    await this.api.delete(`/purchase-requests/${id}`);
  }

  async cancelPurchaseRequest(id: number): Promise<void> {
    await this.api.post(`/purchase-requests/${id}/cancel`);
  }

  // Production
  async getProductionCapacity(): Promise<{ items: { productId: number; productName: string; buildable: number }[] }> {
    const response = await this.api.get('/production/capacity');
    return response.data as any;
  }

  async getProductionCosts(): Promise<{ items: { productId: number; productName: string; unitCost: number }[] }> {
    const response = await this.api.get('/production/costs');
    return response.data as any;
  }

  // Health check
  async health(): Promise<{ status: string; timestamp: string; service: string }> {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Fixed expenses (settings)
  async getFixedExpenses(): Promise<FixedExpenseRow[]> {
    const res = await this.api.get('/settings/fixed-expenses');
    return res.data || [];
  }
  async createFixedExpense(payload: FixedExpenseInput): Promise<FixedExpenseRow> {
    const res = await this.api.post('/settings/fixed-expenses', payload);
    return res.data;
  }
  async updateFixedExpense(id: number, payload: FixedExpenseInput): Promise<FixedExpenseRow> {
    const res = await this.api.put(`/settings/fixed-expenses/${id}`, payload);
    return res.data;
  }
  async deleteFixedExpense(id: number): Promise<void> {
    await this.api.delete(`/settings/fixed-expenses/${id}`);
  }

  // One-time expenses (del día a día) y deudas a proveedores sin orden de compra
  async getOneTimeExpenses(params?: { from?: string; to?: string; pendingOnly?: boolean }): Promise<OneTimeExpenseRow[]> {
    const res = await this.api.get('/one-time-expenses', { params });
    return res.data || [];
  }
  async createOneTimeExpense(payload: OneTimeExpenseInput): Promise<OneTimeExpenseRow> {
    const res = await this.api.post('/one-time-expenses', payload);
    return res.data;
  }
  async updateOneTimeExpense(id: number, payload: OneTimeExpenseInput): Promise<OneTimeExpenseRow> {
    const res = await this.api.put(`/one-time-expenses/${id}`, payload);
    return res.data;
  }
  async deleteOneTimeExpense(id: number): Promise<void> {
    await this.api.delete(`/one-time-expenses/${id}`);
  }
  async payOneTimeExpense(id: number, payload: { amount: number; date?: string }): Promise<OneTimeExpenseRow> {
    const res = await this.api.post(`/one-time-expenses/${id}/payment`, payload);
    return res.data;
  }
  async getExpenseCategories(): Promise<string[]> { return (await this.api.get('/expense-categories')).data || []; }
  async setExpenseCategories(names: string[]): Promise<string[]> { return (await this.api.put('/expense-categories', names)).data || []; }

  // Profit (stats)
  async getProfit(period: 'day'|'week'|'month' = 'month'): Promise<{ period: string; start: string; end: string; revenue: number; cogs: number; grossProfit: number; fixedAllocated: number; netProfit: number; monthlyFixed: number }>{
    const res = await this.api.get('/stats/profit', { params: { period } });
    return res.data;
  }

  // Generic request method for custom endpoints
  async request<T>(config: AxiosRequestConfig): Promise<T> {
    const response = await this.api.request<T>(config);
    return response.data;
  }

  // ==================== Calendar ====================

  async getCalendarEvents(startDate: string, endDate: string, categories?: string[]): Promise<CalendarEvent[]> {
    const params: any = { startDate, endDate };
    if (categories && categories.length > 0) {
      params.categories = categories.join(',');
    }
    const response = await this.api.get('/calendar/events', { params });
    return (response.data?.data as CalendarEvent[]) || [];
  }

  async getLiabilitiesSummary(): Promise<LiabilitiesSummary> {
    const response = await this.api.get('/calendar/liabilities-summary');
    return response.data?.data as LiabilitiesSummary;
  }

  async getReceivablesSummary(): Promise<ReceivablesSummary> {
    const response = await this.api.get('/calendar/receivables-summary');
    return response.data?.data as ReceivablesSummary;
  }

  async createCalendarEvent(event: { title: string; date: string; endDate?: string; category?: string; color?: string; amount?: number; currency?: string; status?: string; description?: string; flowType?: 'income' | 'expense' }): Promise<CalendarEvent> {
    const response = await this.api.post('/calendar/events', event);
    return response.data?.data as CalendarEvent;
  }

  async updateCalendarEvent(id: number, event: { title?: string; date?: string; endDate?: string; category?: string; color?: string; amount?: number; currency?: string; status?: string; description?: string }): Promise<CalendarEvent> {
    const response = await this.api.put(`/calendar/events/${id}`, event);
    return response.data?.data as CalendarEvent;
  }

  async deleteCalendarEvent(id: number): Promise<void> {
    await this.api.delete(`/calendar/events/${id}`);
  }

  // ==================== Seguimiento comercial ====================

  async getCommercialToday(params?: { collectDaysAhead?: number; coverageDays?: number }): Promise<CommercialToday> {
    const response = await this.api.get('/commercial/today', { params });
    return response.data as CommercialToday;
  }

  async getStockCoverage(params?: { coverageDays?: number; includeAll?: boolean }): Promise<StockCoverage[]> {
    const response = await this.api.get('/commercial/stock-coverage', { params });
    return (response.data as StockCoverage[]) || [];
  }

  async getCustomerCalls(customerId: number): Promise<CallEvent[]> {
    const response = await this.api.get(`/commercial/customers/${customerId}/calls`);
    return (response.data as CallEvent[]) || [];
  }

  async scheduleCall(payload: { customerId: number; inDays?: number; date?: string; notes?: string }): Promise<CallEvent> {
    const response = await this.api.post('/commercial/calls', payload);
    return response.data as CallEvent;
  }

  async completeCall(id: number, payload: { notes?: string; nextInDays?: number; nextDate?: string; nextNotes?: string }): Promise<{ completed: CallEvent; next: CallEvent | null }> {
    const response = await this.api.post(`/commercial/calls/${id}/complete`, payload);
    return response.data;
  }

  async rescheduleCall(id: number, payload: { inDays?: number; date?: string }): Promise<CallEvent> {
    const response = await this.api.post(`/commercial/calls/${id}/reschedule`, payload);
    return response.data as CallEvent;
  }

  async quickSale(payload: { customerId?: number; items: { productId: number; quantity: number; unitPrice?: number }[]; paidNow: boolean; paymentMethod?: string; dueInDays?: number; notes?: string; displaysDelivered?: number; channel?: string; delivered?: boolean; estimatedDelivery?: string }): Promise<{ orderId: number; reference: string; total: number; paidNow: boolean; paymentDueDate?: string | null; warning?: string | null }> {
    const response = await this.api.post('/mobile/quick-sale', payload);
    return response.data;
  }

  async getPendingDeliveries(): Promise<PendingDelivery[]> { return (await this.api.get('/mobile/pending-deliveries')).data || []; }
  async deliverOrder(id: number, deliveryDate?: string): Promise<void> { await this.api.post(`/mobile/orders/${id}/deliver`, { deliveryDate }); }

  // ==================== Suite comercial (precios, combos, canales, plan, tablero) ====================

  async getPriceLists(): Promise<any[]> { return (await this.api.get('/pricing/lists')).data; }
  async createPriceList(p: { name: string; adjustmentPct: number; isDefault: boolean }): Promise<{ id: number }> { return (await this.api.post('/pricing/lists', p)).data; }
  async updatePriceList(id: number, p: { name: string; adjustmentPct: number; isDefault: boolean }): Promise<void> { await this.api.put(`/pricing/lists/${id}`, p); }
  async deletePriceList(id: number): Promise<void> { await this.api.delete(`/pricing/lists/${id}`); }
  async setPriceListItems(id: number, items: { productId: number; price: number }[]): Promise<void> { await this.api.put(`/pricing/lists/${id}/items`, items); }
  async getCustomerPrices(customerId: number): Promise<any[]> { return (await this.api.get(`/pricing/customers/${customerId}`)).data; }
  async setCustomerPrices(customerId: number, items: { productId: number; price: number; suggestedRetailPrice?: number | null }[]): Promise<void> { await this.api.put(`/pricing/customers/${customerId}`, items); }
  /** Cambios de precio (lista sin cliente, pactado con cliente), del más nuevo al más viejo. */
  async getPriceHistory(params: { productId?: number; customerId?: number; listOnly?: boolean } = {}): Promise<PriceHistoryRow[]> {
    return (await this.api.get('/pricing/history', { params })).data;
  }
  async resolvePrices(customerId?: number): Promise<{ productId: number; productName: string; listPrice: number; price: number; source: string }[]> {
    return (await this.api.get('/pricing/resolve', { params: { customerId } })).data;
  }
  async getCombos(): Promise<{ productId: number; productName: string; components: { componentProductId: number; componentName: string; quantity: number }[] }[]> { return (await this.api.get('/pricing/combos')).data; }
  async setCombo(productId: number, components: { componentProductId: number; quantity: number }[]): Promise<void> { await this.api.put(`/pricing/combos/${productId}`, components); }

  async stockOut(p: { productId: number; quantity: number; reason: 'GIFT' | 'LOSS'; customerId?: number; notes?: string }): Promise<void> { await this.api.post('/mobile/stock-out', p); }

  async getUninvoiced(): Promise<any[]> { return (await this.api.get('/commercial/uninvoiced')).data; }
  async markInvoiced(orderId: number, receiptNumber?: string): Promise<void> { await this.api.post(`/commercial/orders/${orderId}/mark-invoiced`, { receiptNumber }); }

  async getCommercialSettings(): Promise<any> { return (await this.api.get('/commercial/settings')).data; }
  async saveCommercialSettings(p: { usdRate: number; monthlySalesGoal: number; monthlyUnitsGoal: number; expenseRatioLimit: number }): Promise<{ updatedMaterials: number }> { return (await this.api.put('/commercial/settings', p)).data; }
  async getGoalSuggestion(p: { salesGoal?: number; unitsGoal?: number }): Promise<{ month: string; revenue: number; units: number; averageUnitPrice: number | null; suggestedUnitsGoal: number | null; suggestedSalesGoal: number | null }> { return (await this.api.get('/commercial/goal-suggestion', { params: p })).data; }
  async setMaterialsUsd(items: { materialId: number; costUsd: number | null }[]): Promise<void> { await this.api.put('/commercial/materials-usd', items); }

  async getBudgets(): Promise<{ category: string; monthlyAmount: number }[]> { return (await this.api.get('/commercial/budgets')).data; }
  async setBudgets(items: { category: string; monthlyAmount: number }[]): Promise<void> { await this.api.put('/commercial/budgets', items); }
  async getExpenseBudget(month?: string): Promise<any> { return (await this.api.get('/commercial/expense-budget', { params: { month } })).data; }
  async getExpensesMonthly(months = 12): Promise<ExpensesMonthly> { return (await this.api.get('/commercial/expenses-monthly', { params: { months } })).data; }

  async getChannelCosts(): Promise<{ rules: any[]; channels: string[]; paymentMethods: string[] }> { return (await this.api.get('/commercial/channel-costs')).data; }
  async setChannelCosts(rules: any[]): Promise<void> { await this.api.put('/commercial/channel-costs', rules); }
  async getChannelProfitability(params?: { from?: string; to?: string }): Promise<any> { return (await this.api.get('/commercial/channel-profitability', { params })).data; }

  async getRepurchaseStatus(): Promise<{ customerId: number; status: 'NUEVO' | 'ACTIVO' | 'PROXIMO' | 'CONTACTAR'; orderCount: number; daysSinceLastOrder: number; avgDaysBetweenOrders?: number | null }[]> {
    return (await this.api.get('/commercial/repurchase-status')).data;
  }
  async getProductionPlan(params?: { mode?: 'justo' | 'conservador'; leadDays?: number }): Promise<any> { return (await this.api.get('/commercial/production-plan', { params })).data; }
  async setProductPlanning(id: number, p: { minStock: number; reorderPoint: number; productionBatchSize: number }): Promise<void> { await this.api.put(`/commercial/products/${id}/planning`, p); }
  async getCommercialDashboard(months?: number): Promise<any> { return (await this.api.get('/commercial/dashboard', { params: { months } })).data; }
  async importSpreadsheet(payload: any): Promise<Record<string, number>> { return (await this.api.post('/import/spreadsheet', payload, { timeout: 300000 })).data; }

  // ==================== Vertical fábrica (flag "factory-suite") ====================
  async getMachines(includeInactive = false): Promise<Machine[]> { return (await this.api.get('/factory/machines', { params: { includeInactive } })).data; }
  async createMachine(m: SaveMachine): Promise<Machine> { return (await this.api.post('/factory/machines', m)).data; }
  async updateMachine(id: number, m: SaveMachine): Promise<Machine> { return (await this.api.put(`/factory/machines/${id}`, m)).data; }
  async deleteMachine(id: number): Promise<void> { await this.api.delete(`/factory/machines/${id}`); }
  async getShiftEntries(params: { from?: string; to?: string; machineId?: number; productId?: number }): Promise<ShiftEntry[]> { return (await this.api.get('/factory/shifts', { params })).data; }
  async createShiftEntry(e: SaveShiftEntry): Promise<ShiftEntry> { return (await this.api.post('/factory/shifts', e)).data; }
  async updateShiftEntry(id: number, e: SaveShiftEntry): Promise<ShiftEntry> { return (await this.api.put(`/factory/shifts/${id}`, e)).data; }
  async deleteShiftEntry(id: number): Promise<void> { await this.api.delete(`/factory/shifts/${id}`); }
  async getProductionKpis(params: { from?: string; to?: string }): Promise<ProductionKpis> { return (await this.api.get('/factory/production-kpis', { params })).data; }

  async getFactoryCosts(): Promise<FactoryCosts> { return (await this.api.get('/factory/costs')).data; }
  async saveFactoryCostSettings(s: Partial<FactoryCostSettings>): Promise<FactoryCostSettings> { return (await this.api.put('/factory/cost-settings', s)).data; }
  async saveProductCostProfile(productId: number, p: SaveProductCostProfile): Promise<ProductCost> { return (await this.api.put(`/factory/costs/${productId}`, p)).data; }
  async deleteProductCostProfile(productId: number): Promise<void> { await this.api.delete(`/factory/costs/${productId}`); }
  async applyProductCost(productId: number, applyPrice = false): Promise<ProductCost> { return (await this.api.post(`/factory/costs/${productId}/apply`, null, { params: { applyPrice } })).data; }

  async getOutworkWorkers(includeInactive = false): Promise<OutworkWorker[]> { return (await this.api.get('/factory/outwork/workers', { params: { includeInactive } })).data; }
  async createOutworkWorker(w: SaveOutworkWorker): Promise<OutworkWorker> { return (await this.api.post('/factory/outwork/workers', w)).data; }
  async updateOutworkWorker(id: number, w: SaveOutworkWorker): Promise<void> { await this.api.put(`/factory/outwork/workers/${id}`, w); }
  async deleteOutworkWorker(id: number): Promise<void> { await this.api.delete(`/factory/outwork/workers/${id}`); }
  async getOutworkMovements(params: { workerId?: number; from?: string; to?: string }): Promise<OutworkMovement[]> { return (await this.api.get('/factory/outwork/movements', { params })).data; }
  async createOutworkMovements(p: { date: string; type: OutworkType; productId?: number | null; notes?: string; lines: { workerId: number; packages: number }[] }): Promise<void> { await this.api.post('/factory/outwork/movements', p); }
  async deleteOutworkMovement(id: number): Promise<void> { await this.api.delete(`/factory/outwork/movements/${id}`); }
  async getOutworkRates(): Promise<OutworkRate[]> { return (await this.api.get('/factory/outwork/rates')).data; }
  async createOutworkRate(r: Omit<OutworkRate, 'id' | 'productName'>): Promise<void> { await this.api.post('/factory/outwork/rates', r); }
  async deleteOutworkRate(id: number): Promise<void> { await this.api.delete(`/factory/outwork/rates/${id}`); }
  async getOutworkSettlement(from: string, to: string): Promise<OutworkSettlement> { return (await this.api.get('/factory/outwork/settlement', { params: { from, to } })).data; }
  async getOutworkPayments(params: { workerId?: number; from?: string; to?: string }): Promise<OutworkPayment[]> { return (await this.api.get('/factory/outwork/payments', { params })).data; }
  async createOutworkPayments(payments: OutworkPayment[]): Promise<void> { await this.api.post('/factory/outwork/payments', payments); }
  async deleteOutworkPayment(id: number): Promise<void> { await this.api.delete(`/factory/outwork/payments/${id}`); }

  async getCustomerPlants(customerId?: number): Promise<CustomerPlant[]> { return (await this.api.get('/factory/plants', { params: { customerId } })).data; }
  async createCustomerPlant(p: Omit<CustomerPlant, 'id'>): Promise<void> { await this.api.post('/factory/plants', p); }
  async updateCustomerPlant(id: number, p: Omit<CustomerPlant, 'id'>): Promise<void> { await this.api.put(`/factory/plants/${id}`, p); }
  async deleteCustomerPlant(id: number): Promise<void> { await this.api.delete(`/factory/plants/${id}`); }
  async getCustomerOrders(params: { customerId?: number; status?: string }): Promise<CustomerOrder[]> { return (await this.api.get('/factory/customer-orders', { params })).data; }
  async getCustomerOrder(id: number): Promise<CustomerOrder> { return (await this.api.get(`/factory/customer-orders/${id}`)).data; }
  async createCustomerOrder(o: SaveCustomerOrder): Promise<CustomerOrder> { return (await this.api.post('/factory/customer-orders', o)).data; }
  async updateCustomerOrder(id: number, o: SaveCustomerOrder): Promise<CustomerOrder> { return (await this.api.put(`/factory/customer-orders/${id}`, o)).data; }
  async deleteCustomerOrder(id: number): Promise<void> { await this.api.delete(`/factory/customer-orders/${id}`); }
  async addCustomerOrderDelivery(id: number, d: SaveCustomerOrderDelivery): Promise<CustomerOrder> { return (await this.api.post(`/factory/customer-orders/${id}/deliveries`, d)).data; }
  async deleteCustomerOrderDelivery(id: number, deliveryId: number): Promise<CustomerOrder> { return (await this.api.delete(`/factory/customer-orders/${id}/deliveries/${deliveryId}`)).data; }
  /** Venta en cuenta corriente para una entrega de OC (mismo circuito que una venta cargada a mano). */
  async createOrderForDelivery(p: { customerId: number; reference?: string; notes?: string; items: { productId: number; quantity: number; unitPrice: number }[] }): Promise<{ id: number; reference: string }> {
    return (await this.api.post('/orders', p)).data;
  }

  async getDebtPlans(includeInactive = false): Promise<DebtPlan[]> { return (await this.api.get('/factory/debts', { params: { includeInactive } })).data; }
  async getDebtPlan(id: number): Promise<DebtPlan> { return (await this.api.get(`/factory/debts/${id}`)).data; }
  async createDebtPlan(p: SaveDebtPlan): Promise<DebtPlan> { return (await this.api.post('/factory/debts', p)).data; }
  async updateDebtPlan(id: number, p: SaveDebtPlan): Promise<DebtPlan> { return (await this.api.put(`/factory/debts/${id}`, p)).data; }
  async deleteDebtPlan(id: number): Promise<void> { await this.api.delete(`/factory/debts/${id}`); }
  async addDebtInstallments(id: number, g: GenerateInstallments): Promise<DebtPlan> { return (await this.api.post(`/factory/debts/${id}/installments`, g)).data; }
  async updateDebtInstallment(id: number, i: Omit<DebtInstallment, 'id'>): Promise<void> { await this.api.put(`/factory/debts/installments/${id}`, i); }
  async payDebtInstallment(id: number, p?: { amount?: number; paidAt?: string; notes?: string }): Promise<void> { await this.api.post(`/factory/debts/installments/${id}/pay`, p ?? {}); }
  async deleteDebtInstallment(id: number): Promise<void> { await this.api.delete(`/factory/debts/installments/${id}`); }
  async getDebtSummary(months = 12): Promise<DebtSummary> { return (await this.api.get('/factory/debts/summary', { params: { months } })).data; }
  /** Marca la venta entregada: descuenta el stock una sola vez (la factura no lo vuelve a tocar). */
  async markOrderDelivered(id: number): Promise<void> { await this.api.post(`/orders/${id}/status`, { status: 'Delivered' }); }

  async getMaintenanceOverview(): Promise<EquipmentOverview[]> { return (await this.api.get('/factory/maintenance/overview')).data; }
  async getMaintenancePlans(machineId?: number): Promise<MaintenancePlan[]> { return (await this.api.get('/factory/maintenance/plans', { params: { machineId } })).data; }
  async createMaintenancePlan(p: SaveMaintenancePlan): Promise<void> { await this.api.post('/factory/maintenance/plans', p); }
  async updateMaintenancePlan(id: number, p: SaveMaintenancePlan): Promise<void> { await this.api.put(`/factory/maintenance/plans/${id}`, p); }
  async deleteMaintenancePlan(id: number): Promise<void> { await this.api.delete(`/factory/maintenance/plans/${id}`); }
  async scheduleMaintenancePlan(id: number, p: { plannedDate: string; responsible?: string | null; estimatedDowntimeHours?: number | null }): Promise<void> { await this.api.post(`/factory/maintenance/plans/${id}/schedule`, p); }
  async getMaintenanceRecords(params: { machineId?: number; status?: string; from?: string; to?: string }): Promise<MaintenanceRecord[]> { return (await this.api.get('/factory/maintenance/records', { params })).data; }
  async createMaintenanceRecord(r: SaveMaintenanceRecord): Promise<void> { await this.api.post('/factory/maintenance/records', r); }
  async updateMaintenanceRecord(id: number, r: SaveMaintenanceRecord): Promise<void> { await this.api.put(`/factory/maintenance/records/${id}`, r); }
  async deleteMaintenanceRecord(id: number): Promise<void> { await this.api.delete(`/factory/maintenance/records/${id}`); }
  async startMaintenanceRecord(id: number): Promise<void> { await this.api.post(`/factory/maintenance/records/${id}/start`); }
  async completeMaintenanceRecord(id: number, c: CompleteMaintenance): Promise<void> { await this.api.post(`/factory/maintenance/records/${id}/complete`, c); }
  async getMaintenanceCalendar(from?: string, to?: string): Promise<MaintenanceCalendarItem[]> { return (await this.api.get('/factory/maintenance/calendar', { params: { from, to } })).data; }

  async getStockStatus(): Promise<StockStatus> { return (await this.api.get('/factory/stock-status')).data; }
  async setProductStockLevels(id: number, l: StockLevels): Promise<void> { await this.api.put(`/factory/stock-status/products/${id}`, l); }
  async setMaterialStockLevels(id: number, l: StockLevels): Promise<void> { await this.api.put(`/factory/stock-status/materials/${id}`, l); }
  async planProduction(lines: PlanLine[]): Promise<PlanResult> { return (await this.api.post('/factory/plan', { lines })).data; }

  async getStorageLocations(includeInactive = false): Promise<StorageLocation[]> { return (await this.api.get('/factory/storage/locations', { params: { includeInactive } })).data; }
  async createStorageLocation(l: Partial<StorageLocation>): Promise<void> { await this.api.post('/factory/storage/locations', l); }
  async updateStorageLocation(id: number, l: Partial<StorageLocation>): Promise<void> { await this.api.put(`/factory/storage/locations/${id}`, l); }
  async deleteStorageLocation(id: number): Promise<void> { await this.api.delete(`/factory/storage/locations/${id}`); }
  async getStorageItems(params: { search?: string; kind?: 'product' | 'material' }): Promise<StorageItem[]> { return (await this.api.get('/factory/storage/items', { params })).data; }
  async getLocationStock(id: number): Promise<StorageItem[]> { return (await this.api.get(`/factory/storage/locations/${id}/stock`)).data; }
  async getLocationMovements(params: { from?: string; to?: string; locationId?: number }): Promise<LocationMovement[]> { return (await this.api.get('/factory/storage/movements', { params })).data; }
  async createLocationMovement(m: SaveLocationMovement): Promise<void> { await this.api.post('/factory/storage/movements', m); }

  async getMaterialIssues(params: { from?: string; to?: string; materialId?: number; productId?: number }): Promise<MaterialIssue[]> { return (await this.api.get('/factory/material-issues', { params })).data; }
  async createMaterialIssues(p: { date: string; productId?: number | null; machineId?: number | null; document?: string; notes?: string; lines: { materialId: number; quantity: number }[] }): Promise<void> { await this.api.post('/factory/material-issues', p); }
  async deleteMaterialIssue(id: number): Promise<void> { await this.api.delete(`/factory/material-issues/${id}`); }
  async getMaterialUses(): Promise<MaterialUse[]> { return (await this.api.get('/factory/material-uses')).data; }
  async createMaterialUse(u: Omit<MaterialUse, 'id'>): Promise<void> { await this.api.post('/factory/material-uses', u); }
  async deleteMaterialUse(id: number): Promise<void> { await this.api.delete(`/factory/material-uses/${id}`); }

  // Materia prima por peso: recepción con foto del visor, mezclas y trazabilidad
  async readScale(file: Blob): Promise<ScaleReadResult> {
    const form = new FormData();
    form.append('file', file, 'visor.jpg');
    return (await this.api.post('/factory/receptions/read-scale', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000 })).data;
  }
  async getFactoryPhoto(id: number): Promise<Blob> { return (await this.api.get(`/factory/photos/${id}`, { responseType: 'blob' })).data; }
  async getReceptions(params: { from?: string; to?: string; materialId?: number }): Promise<Reception[]> { return (await this.api.get('/factory/receptions', { params })).data; }
  async createReception(r: SaveReception): Promise<Reception> { return (await this.api.post('/factory/receptions', r)).data; }
  async linkReceptionDocument(id: number, d: { remitoNumber?: string; documentKg?: number | null; differenceTreatment?: DifferenceTreatment | null }): Promise<Reception> { return (await this.api.put(`/factory/receptions/${id}/document`, d)).data; }
  async getCostClose(period: string): Promise<CostClose> { return (await this.api.get(`/factory/cost-close/${period}`)).data; }
  async saveCostClose(period: string, c: SaveCostClose): Promise<CostClose> { return (await this.api.put(`/factory/cost-close/${period}`, c)).data; }
  async getFactoryResults(params: { months?: number; productMonths?: number } = {}): Promise<FactoryResults> { return (await this.api.get('/factory/results', { params })).data; }
  async getSalesPrices(params: { from?: string; to?: string } = {}): Promise<SalesPrices> { return (await this.api.get('/factory/prices', { params })).data; }
  async getTaxBookSummary(params: { from?: string; to?: string } = {}): Promise<TaxBookSummary> { return (await this.api.get('/factory/tax-book/summary', { params })).data; }
  async getTaxBook(params: { book?: string; period?: string; search?: string } = {}): Promise<TaxBookEntry[]> { return (await this.api.get('/factory/tax-book', { params })).data; }
  async getGrindingTrips(params: { from?: string; to?: string } = {}): Promise<GrindingTrip[]> { return (await this.api.get('/factory/grinding', { params })).data; }
  async sendToGrinding(g: SaveGrindingOut): Promise<GrindingTrip> { return (await this.api.post('/factory/grinding', g)).data; }
  async returnFromGrinding(id: number, g: SaveGrindingReturn): Promise<GrindingTrip> { return (await this.api.post(`/factory/grinding/${id}/return`, g)).data; }
  async deleteGrindingTrip(id: number, onlyReturn = false): Promise<void> { await this.api.delete(`/factory/grinding/${id}`, { params: { onlyReturn } }); }
  // Inyectoras en vivo
  async getTelemetryLive(): Promise<TelemetryLive> { return (await this.api.get('/factory/telemetry/live')).data; }
  async setStopReason(id: number, p: { reason: string | null; note?: string | null }): Promise<MachineStopView> { return (await this.api.put(`/factory/telemetry/stops/${id}/reason`, p)).data; }
  async connectMachine(id: number, rotate = false): Promise<MachineConnection> { return (await this.api.post(`/factory/telemetry/machines/${id}/connect`, null, { params: { rotate } })).data; }
  async getMachineConnection(id: number): Promise<MachineConnection> { return (await this.api.get(`/factory/telemetry/machines/${id}/connect`)).data; }
  async disconnectMachine(id: number): Promise<void> { await this.api.delete(`/factory/telemetry/machines/${id}/connect`); }
  async mountMold(id: number, machineId: number): Promise<Machine> { return (await this.api.post(`/factory/machines/${id}/mount`, { machineId })).data; }
  async unmountMold(id: number, p: { location?: string; toMaintenance?: boolean }): Promise<Machine> { return (await this.api.post(`/factory/machines/${id}/unmount`, p)).data; }
  async deleteReception(id: number): Promise<void> { await this.api.delete(`/factory/receptions/${id}`); }
  async getFactoryMaterials(): Promise<FactoryMaterial[]> { return (await this.api.get('/factory/mp')).data; }
  async setMaterialOrigin(id: number, origin: MaterialOrigin | null): Promise<void> { await this.api.put(`/factory/mp/${id}/origin`, { origin }); }
  async getMaterialLots(params: { materialId?: number; onlyAvailable?: boolean }): Promise<MaterialLot[]> { return (await this.api.get('/factory/mp-lots', { params })).data; }
  async getMixRecipes(): Promise<MixRecipe[]> { return (await this.api.get('/factory/mix-recipes')).data; }
  async saveMixRecipe(r: Omit<MixRecipe, 'id'> & { id?: number }): Promise<MixRecipe> {
    return (r.id ? await this.api.put(`/factory/mix-recipes/${r.id}`, r) : await this.api.post('/factory/mix-recipes', r)).data;
  }
  async deleteMixRecipe(id: number): Promise<void> { await this.api.delete(`/factory/mix-recipes/${id}`); }
  async getMixBatches(params: { from?: string; to?: string }): Promise<MixBatch[]> { return (await this.api.get('/factory/mix-batches', { params })).data; }
  async createMixBatch(b: { date?: string; mixRecipeId?: number | null; machineId?: number | null; productId?: number | null; totalKg?: number | null; lines?: { materialId: number; kg: number }[]; notes?: string }): Promise<MixBatch> {
    return (await this.api.post('/factory/mix-batches', b)).data;
  }
  async deleteMixBatch(id: number): Promise<void> { await this.api.delete(`/factory/mix-batches/${id}`); }
  async traceMix(id: number): Promise<MixTrace> { return (await this.api.get(`/factory/trace/mix/${id}`)).data; }
  async traceLot(id: number): Promise<LotTrace> { return (await this.api.get(`/factory/trace/lot/${id}`)).data; }

  // Lotes de insumo (pesadas a la salida de máquina), pallets de armado y plan de máquinas
  async getLotConfigs(): Promise<ProductLotConfig[]> { return (await this.api.get('/factory/lot-configs')).data; }
  async saveLotConfig(c: ProductLotConfig): Promise<ProductLotConfig> { return (await this.api.put(`/factory/lot-configs/${c.productId}`, c)).data; }
  async deleteLotConfig(productId: number): Promise<void> { await this.api.delete(`/factory/lot-configs/${productId}`); }
  async getProductLots(params: { kind?: string; status?: string; productId?: number; machineId?: number; from?: string; to?: string; available?: boolean } = {}): Promise<ProductLot[]> {
    return (await this.api.get('/factory/product-lots', { params })).data;
  }
  async traceProductLotByCode(code: string): Promise<ProductLotTrace> { return (await this.api.get(`/factory/product-lots/by-code/${encodeURIComponent(code.trim())}`)).data; }
  async traceProductLot(id: number): Promise<ProductLotTrace> { return (await this.api.get(`/factory/product-lots/${id}/trace`)).data; }
  async openProductLot(l: OpenLot): Promise<ProductLot> { return (await this.api.post('/factory/product-lots', l)).data; }
  async weighIntoLot(w: WeighIntoLot): Promise<WeighResult> { return (await this.api.post('/factory/product-lots/weigh', w)).data; }
  async deleteLotWeighing(id: number): Promise<void> { await this.api.delete(`/factory/product-lots/weighings/${id}`); }
  async closeProductLot(id: number): Promise<ProductLot> { return (await this.api.post(`/factory/product-lots/${id}/close`)).data; }
  async deleteProductLot(id: number): Promise<void> { await this.api.delete(`/factory/product-lots/${id}`); }
  async createPallet(p: CreatePallet): Promise<ProductLotTrace> { return (await this.api.post('/factory/pallets', p)).data; }
  async dispatchPallet(id: number, d: { customerId?: number | null; note?: string | null }): Promise<ProductLot> { return (await this.api.post(`/factory/pallets/${id}/dispatch`, d)).data; }
  async undoDispatchPallet(id: number): Promise<ProductLot> { return (await this.api.delete(`/factory/pallets/${id}/dispatch`)).data; }
  async deletePallet(id: number): Promise<void> { await this.api.delete(`/factory/pallets/${id}`); }
  async getMachineSchedules(includePast = false): Promise<MachineSchedule[]> { return (await this.api.get('/factory/machine-schedules', { params: { includePast } })).data; }
  async createMachineSchedule(s: SaveMachineSchedule): Promise<MachineSchedule> { return (await this.api.post('/factory/machine-schedules', s)).data; }
  async updateMachineSchedule(id: number, s: SaveMachineSchedule): Promise<MachineSchedule> { return (await this.api.put(`/factory/machine-schedules/${id}`, s)).data; }
  async deleteMachineSchedule(id: number): Promise<void> { await this.api.delete(`/factory/machine-schedules/${id}`); }
  async getScheduleForecast(params: { from?: string; to?: string } = {}): Promise<ScheduleForecast> { return (await this.api.get('/factory/machine-schedules/forecast', { params })).data; }

  async getLotProfitability(params?: { productId?: number; from?: string; to?: string; onlyProduced?: boolean }): Promise<LotProfitabilityReport> {
    const response = await this.api.get('/reports/lot-profitability', { params });
    return response.data as LotProfitabilityReport;
  }

  // ==================== Notifications ====================

  async getNotificationConfig(): Promise<{
    events: { key: string; label: string }[];
    groups: { id: number; name: string; members: { userId?: number | null; email?: string | null; userName?: string | null }[] }[];
    subscriptions: { eventKey: string; groupIds: number[] }[];
    shortExpiryMonths: number;
  }> {
    const res = await this.api.get('/notifications/config');
    return res.data;
  }
  async createNotificationGroup(payload: { name: string; members: { userId?: number | null; email?: string | null }[] }): Promise<{ id: number }> {
    const res = await this.api.post('/notifications/groups', payload);
    return res.data;
  }
  async updateNotificationGroup(id: number, payload: { name: string; members: { userId?: number | null; email?: string | null }[] }): Promise<{ id: number }> {
    const res = await this.api.put(`/notifications/groups/${id}`, payload);
    return res.data;
  }
  async deleteNotificationGroup(id: number): Promise<void> {
    await this.api.delete(`/notifications/groups/${id}`);
  }
  async saveNotificationSubscriptions(items: { eventKey: string; groupIds: number[] }[]): Promise<void> {
    await this.api.put('/notifications/subscriptions', { items });
  }
  async saveNotificationSettings(payload: { shortExpiryMonths: number }): Promise<void> {
    await this.api.put('/notifications/settings', payload);
  }
  async sendNotificationTest(): Promise<{ sent: boolean; to: string; message: string }> {
    const res = await this.api.post('/notifications/test');
    return res.data;
  }

  // ==================== Payment Checks ====================

  async getPaymentChecks(params?: { type?: string; status?: string }): Promise<PaymentCheck[]> {
    const response = await this.api.get('/payment-checks', { params });
    return (response.data?.data as PaymentCheck[]) || [];
  }

  async getPaymentCheck(id: number): Promise<PaymentCheck> {
    const response = await this.api.get(`/payment-checks/${id}`);
    return response.data?.data as PaymentCheck;
  }

  async createPaymentCheck(payload: PaymentCheckCreate): Promise<number> {
    const response = await this.api.post('/payment-checks', payload);
    return response.data?.data as number;
  }

  async updatePaymentCheck(id: number, payload: Partial<PaymentCheck>): Promise<void> {
    await this.api.put(`/payment-checks/${id}`, payload);
  }

  async depositPaymentCheck(id: number): Promise<void> {
    await this.api.post(`/payment-checks/${id}/deposit`);
  }

  async clearPaymentCheck(id: number): Promise<void> {
    await this.api.post(`/payment-checks/${id}/clear`);
  }

  async bouncePaymentCheck(id: number): Promise<void> {
    await this.api.post(`/payment-checks/${id}/bounce`);
  }

  async deletePaymentCheck(id: number): Promise<void> {
    await this.api.delete(`/payment-checks/${id}`);
  }

  // ==================== Order PDFs ====================

  async downloadOrderInvoicePdf(orderId: number): Promise<Blob> {
    const response = await this.api.get(`/orders/${orderId}/invoice.pdf`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async downloadOrderRemitoPdf(orderId: number): Promise<Blob> {
    const response = await this.api.get(`/orders/${orderId}/remito.pdf`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // ==================== Facturación electrónica ARCA ====================

  async getInvoicingConfig(): Promise<InvoicingConfig> {
    const response = await this.api.get('/invoicing/config');
    return response.data;
  }

  async saveInvoicingConfig(cfg: InvoicingConfig): Promise<InvoicingConfig> {
    const response = await this.api.put('/invoicing/config', cfg);
    return response.data;
  }

  async getInvoicingRepresentative(): Promise<RepresentativeInfo> {
    const response = await this.api.get('/invoicing/representative');
    return response.data;
  }

  async verifyArcaConnection(): Promise<VerifyConnectionResult> {
    const response = await this.api.post('/invoicing/verify-connection');
    return response.data;
  }

  async previewInvoice(orderIds: number[]): Promise<InvoicePreview> {
    const response = await this.api.post('/invoicing/preview', { orderIds });
    return response.data;
  }

  async emitInvoice(payload: EmitInvoiceRequest): Promise<FiscalInvoice> {
    const response = await this.api.post('/invoicing/emit', payload);
    return response.data;
  }

  async getFiscalSummary(): Promise<FiscalSummary> {
    const response = await this.api.get('/invoicing/summary');
    return response.data;
  }

  async saveFiscalPeriod(month: string, data: { externalBilled?: number; vatCredit?: number }): Promise<void> {
    await this.api.put(`/invoicing/periods/${month}`, data);
  }

  async downloadFiscalInvoicePdf(invoiceId: number): Promise<Blob> {
    const response = await this.api.get(`/invoicing/${invoiceId}/pdf`, { responseType: 'blob' });
    return response.data;
  }

  // ==================== POS + Caja ====================

  async posLookup(code: string): Promise<PosProduct[]> {
    const response = await this.api.get('/pos/lookup', { params: { code } });
    return response.data;
  }

  async posSale(payload: PosSaleRequest): Promise<PosSaleResult> {
    const response = await this.api.post('/pos/sale', payload);
    return response.data;
  }

  async getCurrentCashSession(): Promise<CashRegisterSummary | null> {
    const response = await this.api.get('/cash-register/current');
    return response.data;
  }

  async openCashRegister(openingAmount: number, notes?: string): Promise<CashRegisterSummary> {
    const response = await this.api.post('/cash-register/open', { openingAmount, notes });
    return response.data;
  }

  async closeCashRegister(declaredAmount: number, notes?: string): Promise<CashRegisterSummary> {
    const response = await this.api.post('/cash-register/close', { declaredAmount, notes });
    return response.data;
  }

  async addCashMovement(payload: { type: 'CashIn' | 'CashOut'; amount: number; paymentMethod?: string; description?: string }): Promise<CashRegisterSummary> {
    const response = await this.api.post('/cash-register/movements', payload);
    return response.data;
  }

  // ==================== Customer Account ====================

  async getCustomerAccountSummary(customerId: number): Promise<CustomerAccountSummary> {
    const response = await this.api.get(`/customers/${customerId}/account/summary`);
    return response.data;
  }

  async getCustomerStatement(customerId: number, from?: string, to?: string): Promise<CustomerStatement> {
    const response = await this.api.get(`/customers/${customerId}/account/statement`, {
      params: { from, to }
    });
    return response.data;
  }

  async getCustomerPendingOrders(customerId: number): Promise<CustomerOrderBalance[]> {
    const response = await this.api.get(`/customers/${customerId}/account/pending-orders`);
    return response.data;
  }

  async getCustomerPayments(customerId: number, page = 1, pageSize = 20): Promise<CustomerPaymentList> {
    const response = await this.api.get(`/customers/${customerId}/account/payments`, {
      params: { page, pageSize }
    });
    return response.data;
  }

  async registerCustomerPayment(customerId: number, payment: RegisterCustomerPayment): Promise<CustomerPaymentDto> {
    const response = await this.api.post(`/customers/${customerId}/account/payments`, payment);
    return response.data;
  }

  async voidCustomerPayment(customerId: number, paymentId: number, reason: string): Promise<CustomerPaymentDto> {
    const response = await this.api.post(`/customers/${customerId}/account/payments/${paymentId}/void`, { reason });
    return response.data;
  }

  async recalculateCustomerBalance(customerId: number): Promise<CustomerAccountSummary> {
    const response = await this.api.post(`/customers/${customerId}/account/recalculate`);
    return response.data;
  }

  // ==================== Supplier Account ====================

  async getSupplierAccountSummary(supplierId: number): Promise<any> {
    const response = await this.api.get(`/suppliers/${supplierId}/account/summary`);
    return response.data;
  }

  async getSupplierPendingPurchases(supplierId: number): Promise<any[]> {
    const response = await this.api.get(`/suppliers/${supplierId}/account/purchases`);
    return response.data;
  }

  async getSupplierPayments(supplierId: number): Promise<any> {
    const response = await this.api.get(`/suppliers/${supplierId}/account/payments`);
    return response.data;
  }

  // ==================== Sales Reports ====================

  async getSalesForecast(months = 3): Promise<SalesForecastResponse> {
    const response = await this.api.get('/stats/sales-forecast', { params: { months } });
    return response.data;
  }

  async getNewVsReturning(months = 12): Promise<{ items: Array<{ month: string; newCustomers: number; newOrders: number; newTotal: number; returningCustomers: number; returningOrders: number; returningTotal: number; unassignedOrders: number; unassignedTotal: number }> }> {
    const response = await this.api.get('/stats/new-vs-returning', { params: { months } });
    return response.data;
  }

  async getYoYComparison(params?: { year?: number; monthsBack?: number }): Promise<YoYComparisonResponse> {
    const response = await this.api.get('/stats/yoy-comparison', { params });
    return response.data;
  }

  async getQuarterlyBreakdown(year?: number): Promise<QuarterlyBreakdownResponse> {
    const response = await this.api.get('/stats/quarterly-breakdown', { params: { year } });
    return response.data;
  }

  async getSalesByCustomerType(params?: { from?: string; to?: string }): Promise<SalesByCustomerTypeResponse> {
    const response = await this.api.get('/stats/sales-by-customer-type', { params });
    return response.data;
  }

  async getStrategicClientReport(params?: { customerId?: number; from?: string; to?: string }): Promise<StrategicClientsReportResponse> {
    const response = await this.api.get('/stats/strategic-client-report', { params });
    return response.data;
  }

  // ==================== Monday.com Import ====================

  async importMondaySales(data: ImportMondaySalesRequest): Promise<ImportMondaySalesResult> {
    const response = await this.api.post('/sales/import/monday-sales', data);
    return response.data;
  }

  // ==================== Import Customers ====================

  async importCustomers(data: import('../types').ImportCustomersRequest): Promise<import('../types').ImportCustomersResult> {
    const response = await this.api.post('/customers/import', data);
    return response.data;
  }

  // ==================== Import Opportunities ====================

  async importOpportunities(data: import('../types').ImportOpportunitiesRequest): Promise<import('../types').ImportOpportunitiesResult> {
    const response = await this.api.post('/opportunities/import', data);
    return response.data;
  }

  // ==================== Import Prospects ====================

  async importProspects(data: import('../types').ImportProspectsRequest): Promise<import('../types').ImportProspectsResult> {
    const response = await this.api.post('/prospects/import', data);
    return response.data;
  }

  // ==================== Auth: /me (tenant onboarding status) ====================

  async getAuthMe(): Promise<{ user: User; tenant: Tenant }> {
    const response = await this.api.get('/auth/me');
    return response.data as { user: User; tenant: Tenant };
  }

  // ==================== Onboarding wizard ====================

  async updateTenantOnboarding(payload: import('../types').TenantOnboardingRequest): Promise<Tenant> {
    const response = await this.api.patch<Tenant>('/tenants/current/onboarding', payload);
    return response.data;
  }

  async uploadTenantLogo(file: File): Promise<{ logoUrl: string }> {
    const form = new FormData();
    form.append('file', file);
    const response = await this.api.post<{ logoUrl: string }>('/tenants/current/logo', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }
}

// Create and export a singleton instance
export const apiService = new ApiService();

// Export commonly used methods for convenience
export const {
  login,
  register,
  validateToken,
  getProducts,
  getProduct,
  createProduct,
  updateProduct,
  deleteProduct,
  getProductStats,
  getProductTraceability,
  getPublicTrace,
  adjustStock,
  health,
} = apiService;

export default apiService;
